import { BulkSelectionPayload } from './tasks';
import type { StoredReportStatus } from './reports';

export enum FilingStatusEnum {
  UNFILED = 'UNFILED',
  FILING = 'FILING',
  SUBMITTED = 'SUBMITTED',
  FILED = 'FILED',
  ISSUE = 'ISSUE',
  PAUSED = 'PAUSED',
  SKIPPED = 'SKIPPED',
  CANCELLED = 'CANCELLED',
}

/** Label for amount_fees — penalty vs interest vs other. Null on legacy rows. */
export enum FeeTypeEnum {
  PENALTY = 'PENALTY',
  INTEREST = 'INTEREST',
  OTHER = 'OTHER',
}

export const FEE_TYPE_LABELS: Record<FeeTypeEnum, string> = {
  [FeeTypeEnum.PENALTY]: 'Penalty',
  [FeeTypeEnum.INTEREST]: 'Interest',
  [FeeTypeEnum.OTHER]: 'Other',
};

export const isFeeType = (value: string | null | undefined): value is FeeTypeEnum =>
  value != null && (Object.values(FeeTypeEnum) as string[]).includes(value);

export const feeTypeLabel = (feeType: string | null | undefined): string | null => {
  if (!feeType) return null;
  if (feeType in FEE_TYPE_LABELS) {
    return FEE_TYPE_LABELS[feeType as FeeTypeEnum];
  }
  return feeType;
};

export const feesDisplayLabel = (feeType: string | null | undefined): string => {
  const typeLabel = feeTypeLabel(feeType);
  return typeLabel ? `Fees (${typeLabel})` : 'Fees';
};

export enum FilingTypeEnum {
  REGULAR = 'REGULAR',
  BACK_FILING = 'BACK_FILING',
  AMENDMENT = 'AMENDMENT',
}

/** Legacy VARCHAR until BackfillFilingIsPrepayment — filter/label dual-read only. */
export const LEGACY_PREPAYMENT_FILING_CATEGORY = 'PREPAYMENT';

export const FilingFrequency = {
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  QUARTERLY_PREPAYMENT: 'QUARTERLY_PREPAYMENT',
  SEMI_ANNUALLY: 'SEMI_ANNUALLY',
  ANNUALLY: 'ANNUALLY',
  ANNUAL_FISCAL_YEAR: 'ANNUAL_FISCAL_YEAR',
  SEMI_MONTHLY: 'SEMI_MONTHLY',
  BI_MONTHLY: 'BI_MONTHLY',
  FOUR_MONTHLY: 'FOUR_MONTHLY',
} as const;

/** Mirrors backend TaxTypeEnum on filing rows. */
export const TaxType = {
  SALES_TAX: 'SALES_TAX',
  USE_TAX: 'USE_TAX',
  SALES_AND_USE_TAX: 'SALES_AND_USE_TAX',
  RETAIL_DELIVERY_FEE: 'RETAIL_DELIVERY_FEE',
} as const;
export type TaxType = (typeof TaxType)[keyof typeof TaxType];

/** Row from GET /admin/filings/deletion-eligible */
export interface FilingDeletableAdmin {
  id: string;
  jurisdiction: string;
  /** ISO country code (optional; pair with `jurisdiction` for STATE, COUNTRY display). */
  country_code?: string | null;
  /** Filing period bounds (ISO); format for UI like other filing tabs: `MMM d, yyyy` each, joined with ` - `. */
  start_date: string;
  end_date: string;
  status: string;
  amount: string;
}

/** One distinct (country, state) pair from deletion-eligible filing metadata (filter dropdown). */
export interface FilingDeletionEligibleJurisdiction {
  country_code: string;
  state_code: string;
}

export interface FilingArtifactRef {
  id: string;
  file_name: string;
}

export interface FilingAdditionalPaymentConfirmation {
  id: string;
  confirmation_number: string;
  artifact: FilingArtifactRef;
  uploaded_by_user_id?: string | null;
  created_at: string;
}

export interface FilingArtifactSlots {
  return_artifact: FilingArtifactRef | null;
  payment_artifact: FilingArtifactRef | null;
  additional_artifacts: FilingArtifactRef[];
  additional_payment_confirmations?: FilingAdditionalPaymentConfirmation[];
}

/** Display-only CDTFA Option 2 overlay for CA May quarterly prepayment filings (KA-1240). */
export type CaMayPrepaymentDisplay = {
  base_tax_liability: string;
  prepayment_amount: string;
  note: string;
};

export interface Filing {
  status: FilingStatusEnum;
  start_date: string;
  end_date: string;
  due_date?: string | null;
  date_filed?: string | null;
  is_manual?: boolean | null;
  state_code?: string | null;
  state_name?: string | null;
  country_code?: string | null;
  amount_calculated: string;
  amount_adjusted: string;
  amount_discounts: string;
  amount_fees: string;
  /** Null on legacy fees; does not affect totals. */
  fee_type?: FeeTypeEnum | null;
  amount_penalties: string;
  amount_tax_collected: string;
  amount_sales: string;
  amount: string;
  /** Gross purchase-side tax. US use tax, or EU/UK reverse-charge self-assessed VAT. */
  amount_use_tax?: string | null;
  amount_input_vat_recoverable?: string | null;
  amount_input_vat_true_up?: string | null;
  /** The rate this filing claimed at, and whether that rate is its year's definitive. */
  input_vat_recovery_rate_applied?: string | null;
  input_vat_recovery_rate_applied_is_definitive?: boolean | null;
  credits_utilized?: string | null;
  tax_remitted: string;
  id: string;
  registration_id: string;
  total_tax_liability: string;
  /** Present on FilingRead when the period taxable aggregate is populated. */
  total_taxable_sales?: string | null;
  currency: string;
  return_confirmation_id?: string;
  payment_confirmation_id?: string;
  /** Parked at Bulk Submit (before Complete); distinct from legacy confirmation IDs. */
  submitted_return_confirmation_id?: string | null;
  submitted_payment_confirmation_id?: string | null;
  payment_date?: string | null;
  internal_notes?: string;
  filing_website_url?: string;
  cancelled_reason?: string | null;
  filing_type?: string; // to be implemented
  attachments?: Record<string, string> | null;
  filing_artifacts?: FilingArtifactSlots | null;
  sources?: string[];
  filing_category?: string;
  /** Orthogonal to filing_category (KA-1729). */
  is_prepayment?: boolean;
  /** Orthogonal to filing_category / is_prepayment — deregistration closing return (KA-1277 / KA-1964). */
  is_final?: boolean;
  tax_type?: TaxType;
  /**
   * True when this is a state Retail Delivery Fee return (separate from sales tax).
   */
  is_rdf?: boolean;
  /** Non-marketplace transaction count from FilingRead; total = this + marketplace_transaction_count. */
  transaction_count?: number;
  /** Marketplace transaction count from FilingRead. */
  marketplace_transaction_count?: number;
  prefer_collected_tax_amounts?: boolean;
  /** Display-only CDTFA Option 2 (135% of May liability) for CA May QP PREPAYMENT filings. */
  ca_may_prepayment?: CaMayPrepaymentDisplay | null;
  /** Display-only P&I remittance: 'Paid with return' or 'State bills you later'. */
  penalty_interest_remittance_tag?: string | null;
}

/** Row from GET /admin/filings/all (cross-org paginated list). */
export interface AdminFilingCrossOrg extends Filing {
  organization_id: string;
  organization_name?: string | null;
  /** Newest enhanced report status for this filing's jurisdiction; null if none. */
  v2_enhanced_report_status?: StoredReportStatus | null;
  /** True when the newest report is DONE but out of date; opening it rebuilds it. */
  v2_enhanced_report_stale?: boolean;
  /** Whether this CT filing has a linked transaction; null outside CT. Without one, OS-114 files zero and reads no report. */
  ct_os114_has_linked_transaction?: boolean | null;
}

export interface Period {
  start_date: string;
  end_date: string;
  filing_category: string;
}

export interface FilingStateOption {
  registration_id: string;
  country_code: string;
  state_name: string;
  state_code: string;
  registration_date: string;
  /**
   * Registration status; flags DEREGISTERED (closed) jurisdictions in New Filing (KA-1280).
   * Typed as string to avoid a types cycle with `./registrations` (which imports this module).
   */
  status?: string;
  label?: string;
  value?: string;
}

export interface TransactionRefund {
  type: string;
  amount: number;
  description: string;
}

// KA-2090 "Excess Tax Captured": over-collected tax grossed onto the statutory
// shared base (a phantom CDP city, or a collection above the canonical rate),
// disclosed source -> target. Informational only -- already inside the grossed-up
// jurisdiction lines, never summed into a total.
export interface ExcessTaxCaptured {
  source_jurisdiction: string;
  target_jurisdiction: string;
  excess_amount: number;
  added_taxable?: number | null;
}

export interface DeductionExemption {
  id?: string;
  code?: string;
  type: string;
  description?: string;
  amount: number;
  product_name?: string;
  customer_name?: string;
}

export interface TaxRateBreakDown {
  tax_rate?: number;
  tax_type?: string;
  gross_sales?: number;
  taxable_sales?: number;
  tax_due?: number;
  actual_tax?: number;
  exemptions?: number;
}

/** One rate tier of a v2 rate schedule: taxable sales and tax at a single rate. */
export interface RateScheduleTier {
  rate: number;
  taxable: number;
  tax: number;
}

/** A taxability tier of a breakdown row, with the combined figures v2 adds. */
export interface BreakdownTier extends RateScheduleTier {
  /**
   * v2 only, on leaves that hide the local columns: state + this tier's local
   * rate, and the tier's state plus local tax. `combined_rate` is null when the
   * tier mixed state rates, so no single rate reproduces it.
   */
  combined_rate?: number | null;
  combined_tax_due?: number | null;
}

/**
 * v2 only: one keyable line of a breakdown row, for one taxability class, rate
 * and stack of local jurisdictions. A row's lines can carry different local
 * stacks, so the row alone cannot be keyed. The sub-rows of one tier add up to
 * its taxable and tax.
 */
export interface JurisdictionSubRow {
  class: string;
  /** The product code our items were priced under (e.g. PHYS_GROCERIES), or null when unknown. */
  product_class?: string | null;
  rate: number;
  taxable: number;
  tax: number;
  jurisdictions: string[];
  /**
   * The jurisdictions joined with " + ", "No local tax" for an empty stack, or
   * "No calculated stack" for lines our items did not price.
   */
  label: string;
  /** Each jurisdiction of the stack, keyable on its own line. Absent on older reports. */
  components?: JurisdictionSubRowComponent[];
}

/** One local jurisdiction of a sub-row's stack, at our calculated rate. */
export interface JurisdictionSubRowComponent {
  name: string;
  /** COUNTY, CITY or SPECIAL. */
  type: string;
  rate: number;
  /** The sub-row's taxable: every component taxes the same base. */
  taxable: number;
  /** Its share of the sub-row's tax by rate. The components add up to the sub-row's tax. */
  tax: number;
}

/**
 * A leaf-declared display column for the v2 admin jurisdiction-breakdown table
 * (KA-1792). A state whose return files a bespoke portal layout (AZ's TPT combined
 * / state-county / city columns, CO's DR-0100 split, TX's rate columns) emits the
 * values as ordinary row fields and declares how to display them here.
 * `JurisdictionBreakdownSectionV2` renders these generically from the payload,
 * keyed on `key` — never on a `stateCode` branch — so per-state presentation stays
 * backend-owned while the shared component stays generic.
 */
export interface PresentationColumn {
  /** The `JurisdictionBreakdown` row field this column reads (e.g. `total_tax_due_combined`). */
  key: string;
  /** Column header text. */
  label: string;
  /** Cell/header alignment; numeric columns are right-aligned. */
  align: 'left' | 'right';
  /** How the row value is formatted. */
  format: 'currency' | 'percentage' | 'text';
}

/** Taxable sales and tax for one reporting taxability class (v2 breakdown rows). */
export interface TaxabilityAmounts {
  taxable: number;
  tax: number;
  /**
   * One tier per rate the class was actually levied at. A jurisdiction can tax
   * the same class at more than one rate (Georgia files grocery sales on 044F at
   * 3% beside non-food on 044 at 4%; a mid-period rate change splits a class the
   * same way), and the row's `local_tax_rate` is only the highest of them — so
   * these are the rates that reproduce the tax beside them.
   */
  tiers?: BreakdownTier[];
}

export interface JurisdictionBreakdown {
  jurisdiction_code: string;
  /** Portal-specific code shown without changing the row's grouping identity. */
  jurisdiction_code_display?: string;
  jurisdiction_name?: string;
  /** v2 only: STATE | COUNTY | CITY | SPECIAL, resolved from the geography. */
  jurisdiction_type?: string;
  /** Portal-specific type shown without changing the row's geography. */
  jurisdiction_type_label?: string;
  gross_sales?: number;
  deductions?: number;
  exemptions?: number;
  marketplace_sales?: number;
  refunds?: number;
  taxable_sales: number;
  /** v2 only: per-taxability-class split of this row's taxable sales and tax. */
  taxability?: Record<string, TaxabilityAmounts>;
  /** v2 only: present when the row's lines carry more than one local stack. */
  sub_rows?: JurisdictionSubRow[];
  tax_amount?: number;
  tax_liability?: number;
  local_tax_rate?: number;
  excess_tax?: number;
  total_tax?: number;
  discount?: number;
  credits?: number;
  tax_due?: number;
  collected_tax?: number;
  county_name?: string;
  // Arizona-specific fields
  total_tax_due_combined?: number;
  state_county_tax?: number;
  city_tax?: number;

  // Texas/North Dakota-specific fields
  total_local_tax_due?: number;
  // Minnesota v2 sends null when the component was levied at more than one rate,
  // or, for the county rate, when the row's county is unknown.
  county_rate?: number | null;
  city_rate?: number | null;

  // Texas-specific fields
  district_tax_rate?: number | null;
  // Nested special districts under cities
  special_districts?: JurisdictionBreakdown[];

  // North Carolina-specific fields
  transit_tax_amount?: number;
  '2_point_25_percent_county_tax_amount'?: number;
  '2_percent_county_tax_amount'?: number;
  food_tax_amount?: number;
  general_sales_amount?: number;
  food_sales_amount?: number;
  /** E-500 Line 12 — Mecklenburg Additional 1% County Rate (periods ≥ 2026-07-01). */
  additional_county_tax_amount?: number;

  // Colorado-specific fields
  state_admin_tax?: number;
  city_admin_tax?: number;
  /** Sales only the other SUTS account taxed; null off home-rule rows. */
  state_exempt_sales?: number | null;
  city_exempt_sales?: number | null;

  // Combined rate states (state + city/county depending on jurisdiction)
  combined_rate?: number;
  combined_tax_due?: number;
}

export interface EnhancedDataSummary {
  total_gross_sales?: number;
  total_state_sales?: number;
  total_deductions_exemptions?: number;
  state_deductions_exemptions?: number | null;
  local_deductions_exemptions?: number | null;
  deductions_exemptions?: number;
  total_marketplace_sales?: number;
  total_taxable_sales?: number;
  taxable_sales?: number;
  taxable_state_sales?: number;
  taxable_local_sales?: number;
  total_tax_amount?: number;
  total_tax_liability?: number;
  total_state_tax?: number;
  total_local_tax?: number;
  total_excess_tax?: number;
  total_tax_collected?: number;
  total_discount?: number;
  total_credits?: number;
  total_tax_due?: number;
  total_transaction_refunds?: number;

  // Absent when the report has no rate schedules: the DR 1786 report of a
  // Colorado retail delivery fee filing (see `isCoRetailDeliveryFeeSummary`).
  /** Taxable sales and tax grouped by state rate. */
  state_schedule?: RateScheduleTier[];
  /** Taxable sales and tax grouped by local rate. */
  local_schedule?: RateScheduleTier[];
  /** Residual between the summed statutory tax items and the liability anchor. */
  collected_vs_statutory_adjustment?: number;
  /**
   * Retail delivery fee inside the liability. A Colorado sales liability never holds
   * it. It is not in state or local tax, so state + local + this fee is the total.
   * Absent when there is no fee.
   */
  retail_delivery_fee?: number;
  /** Taxable sales per reporting taxability class (e.g. `general`, `food`). */
  taxability?: Record<string, number>;

  // CO DR 1786 summary. Present on a retail delivery fee filing. Absent on sales-tax returns.
  number_of_retail_deliveries?: number;
  fee_rate_per_delivery?: number;
  fee_due?: number;
  excess_fee_collected?: number | null;
  total_fee_due?: number | null;
  collected_fee_known?: boolean;
  collected_fee_amount?: number | null;
}

export interface JurisdictionBreakdownGroupedByCounty {
  county_code: string;
  county_name: string;
  // Present on standalone jurisdiction entries (no county_code) within the grouped array
  jurisdiction_code?: string;
  gross_sales?: number;
  deductions?: number;
  exemptions?: number;
  marketplace_sales?: number;
  taxable_sales?: number;
  tax_amount?: number;
  excess_tax?: number;
  total_tax?: number;
  discount?: number;
  credits?: number;
  tax_due?: number;
  local_tax_rate?: number;
  // Arizona-specific fields
  total_tax_due_combined?: number;
  state_county_tax?: number;
  city_tax?: number;
  // Texas/North Dakota-specific fields
  total_local_tax_due?: number;
  county_rate?: number;
  city_rate?: number;
  district_tax_rate?: number;
  // North Carolina-specific fields
  food_sales_amount?: number;
  general_sales_amount?: number;
  food_tax_amount?: number;
  '2_percent_county_tax_amount'?: number;
  '2_point_25_percent_county_tax_amount'?: number;
  transit_tax_amount?: number;
  additional_county_tax_amount?: number;
  // Colorado-specific fields
  state_admin_tax?: number;
  city_admin_tax?: number;
  // Combined rate states
  combined_rate?: number;
  combined_tax_due?: number;
  cities: JurisdictionBreakdown[];
  special_districts: JurisdictionBreakdown[];
  unincorporated?: Partial<JurisdictionBreakdown>;
  special?: Partial<JurisdictionBreakdown>;
}

export interface EnhancedDataSet {
  summary: EnhancedDataSummary;
  jurisdiction_breakdown: JurisdictionBreakdown[];
  jurisdiction_breakdown_grouped_by_county?: JurisdictionBreakdownGroupedByCounty[];
  deductions_exemptions?: DeductionExemption[];
  transaction_refunds?: TransactionRefund[];
  excess_tax?: ExcessTaxCaptured[];
}

export interface ActualTaxLiability {
  filing_id: string;
  state_code: string;
  state_name: string;
  summary: EnhancedDataSummary;
  jurisdiction_breakdown: JurisdictionBreakdown[];
  jurisdiction_breakdown_grouped_by_county?: JurisdictionBreakdownGroupedByCounty[];
  /**
   * v2 only: leaf-declared portal columns for the breakdown table. Absent for
   * states with no bespoke layout (the UI then renders the generic columns only).
   */
  jurisdiction_presentation_columns?: PresentationColumn[];
  /**
   * v2 only: the return files one combined rate per jurisdiction, so the
   * breakdown table drops its local-only rate and tax-due columns. Absent means
   * show them.
   */
  jurisdiction_hides_local_tax_columns?: boolean;
  // KA-2090: over-collection disclosure; absent when there is no over-collection.
  excess_tax?: ExcessTaxCaptured[];
}

export interface FilingEnhancedData {
  filing_id: string;
  organization_id: string;
  state_code?: string;
  state_name?: string;
  filing_period: {
    start_date: string;
    end_date: string;
  };

  // New nested structures
  calculated?: EnhancedDataSet | null;
  collected?: EnhancedDataSet | null;
  actual_tax_liability?: ActualTaxLiability | null;

  // Legacy fields for backward compatibility (contain calculated data by default)
  summary: EnhancedDataSummary;
  jurisdiction_breakdown: JurisdictionBreakdown[];
  deductions_exemptions?: DeductionExemption[];
  transaction_refunds?: TransactionRefund[];
}

export interface BulkFilingItem {
  registration_id: string;
  country_code: string;
  state_code: string;
  periods: Period[];
}

export interface BulkFilingCreate {
  filings: BulkFilingItem[];
}

export interface BulkFilingResponse {
  total_created: number;
  total_failed: number;
  created_filings?: Array<{
    id: string;
    registration_id: string;
    state_code: string;
  }>;
  failed_filings?: Array<{
    filing_data: {
      filing: {
        state_code: string;
        registration_id: string;
      };
      period: {
        start_date: string;
        end_date: string;
      };
    };
    errors?: Array<{
      type: string;
      message: string;
    }>;
  }>;
}

export type BulkBackfillPeriodAction = 'CREATE' | 'SKIP_DUPLICATE' | 'ERROR';

export interface BulkBackfillRequest {
  registration_id: string;
  start_date: string;
  end_date: string;
  filing_frequency: string;
}

export interface BulkBackfillPeriodOutcome {
  start_date: string;
  end_date: string;
  filing_category: string;
  action: BulkBackfillPeriodAction;
  message?: string | null;
}

export interface BulkBackfillPreviewResponse {
  periods: BulkBackfillPeriodOutcome[];
  to_create: number;
  to_skip: number;
  to_error: number;
}

export interface BulkBackfillCreateResponse {
  created_filings: Array<{ id: string; start_date: string; end_date: string }>;
  skipped: Array<{
    start_date: string;
    end_date: string;
    filing_category: string;
    message?: string | null;
  }>;
  failed: Array<{
    start_date: string;
    end_date: string;
    filing_category: string;
    message: string;
  }>;
  total_created: number;
  total_skipped: number;
  total_failed: number;
  total_requested: number;
}

export type ConfirmFilingPayload = {
  payment_confirmation_id?: string;
  return_confirmation_id?: string;
  amount_adjusted?: number | string;
  amount_fees?: number | string;
  fee_type?: FeeTypeEnum | null;
  amount_penalties?: number | string;
  amount_discounts?: number | string;
};

export type UncompleteFilingPayload = {
  reason: string;
};

export type FilingAmountsPayload = {
  payment_confirmation_id?: string;
  return_confirmation_id?: string;
  amount_adjusted?: number | string;
  amount_fees?: number | string;
  fee_type?: FeeTypeEnum | null;
  amount_penalties?: number | string;
  amount_discounts?: number | string;
};

export enum FilingArtifactTypeEnum {
  RETURN = 'RETURN',
  PAYMENT = 'PAYMENT',
}

export interface BulkRecalculateRequest {
  filing_ids: string[];
  selection?: BulkSelectionPayload;
}

export interface BulkRecalculateFilingsResponse {
  queued: number | null;
  successful_filing_ids: string[];
  failed_filing_ids: string[];
}

export interface BulkConfirmFilingsRequest {
  filing_ids: string[];
  return_confirmation_id?: string | null;
  payment_confirmation_id?: string | null;
}

export interface BulkConfirmFilingFailure {
  filing_id: string;
  reason: string;
}

export interface BulkConfirmFilingsResponse {
  successful_filing_ids: string[];
  failures: BulkConfirmFilingFailure[];
  queued: boolean;
}

export interface BulkDeleteFilingsRequest {
  filing_ids: string[];
}

export interface BulkDeleteFilingFailure {
  filing_id: string;
  reason: string;
}

export interface BulkDeleteFilingsResponse {
  /** Deleted inline in this request; empty when `queued`. */
  deleted: string[];
  /** Accepted for the background worker; only set when `queued`. */
  queued_filing_ids: string[];
  failed: BulkDeleteFilingFailure[];
  deleted_count: number;
  queued: boolean;
}

export interface BulkReplaceFilingArtifactsResponse {
  successful_filing_ids: string[];
  failures: BulkConfirmFilingFailure[];
}

export interface BulkEnhancedDataRebuildRequest {
  state_codes: string[];
  year: number;
  month: number;
  include_submitted_and_filed: boolean;
  /** Also rebuild reports that are current for their filing; they are skipped by default. */
  include_current_reports: boolean;
  dry_run: boolean;
  /** Most builds one run queues; a larger cohort runs in slices. */
  max_filings: number;
}

export interface BulkEnhancedDataRebuildStatesResponse {
  state_codes: string[];
}

/** Counts are set on a dry run only; a real run returns 202 with them null. */
export interface BulkEnhancedDataRebuildResponse {
  dry_run: boolean;
  matched_by_status: Record<string, number> | null;
  skipped_no_transactions: number | null;
  skipped_current: number | null;
  to_rebuild: number | null;
  to_queue_this_run: number | null;
  remaining: number | null;
}
